import Link from 'next/link';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useToggle } from '@hooks/toggle/useToggle';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';

// components
import Typography from '@components/_atoms/Typography';
import DefaultButtonBox from '@components/_molecules/DefaultButtonBox';
import Button from '@components/_atoms/Button';
import SignOutModal from './SignOutModal';

// constants
import { ButtonColor, TypoVariant } from '@constants/atoms';
import { COMPANY_POLICY, COMPANY_USE } from '@constants/routes/routes';
import { gray, primary, red } from '@styles/Colors';
import { MOBILE_MODE } from '@constants/size';
import { Radius } from '@styles/Radius';

// hooks
import useHandleOutsideClickAction from '@hooks/interaction/useHandleOutsideClickAction';
import ButtonCode from '@constants/log/buttonCode';
import { useGetUserColleaguesFetch, useGetUserProfileFetch } from '@hooks/fetch/useUserFetch';

const Layout = styled.div``;

const ContentWrapper = styled.div`
  margin-bottom: 80px;

  padding: 32px;
  border: 1px solid ${gray.gray3};
  border-radius: ${Radius.MEDIUM};

  @media ${MOBILE_MODE} {
    margin-bottom: 104px;
  }
`;

const TitleBox = styled.div``;

const LinkWrapper = styled.div`
  margin-top: 8px;

  display: flex;
  flex-wrap: wrap;
  row-gap: 8px;
  column-gap: 12px;
`;

const ListBox = styled.ul`
  margin-top: 24px;

  display: grid;
  row-gap: 8px;
`;

const ListItem = styled.li`
  margin-left: 24px;
`;

export default function SignOutContent() {
  const { t } = useTranslation('user');
  const router = useRouter();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const openModal = useToggle();

  return (
    <Layout>
      <ContentWrapper>
        <>
          <TitleBox>
            <Typography variant={TypoVariant.SH2} color={primary.gray}>
              {t('signout.subTitle')}
            </Typography>
            <LinkWrapper>
              <Link href={COMPANY_USE}>
                <a target="_blank" data-steelboso-id={ButtonCode.BTN_SERVICE_TERMS}>
                  <Typography style={{ textDecoration: 'underline' }} variant={TypoVariant.B4} color={gray.gray6}>
                    {t('signout.link1')}
                  </Typography>
                </a>
              </Link>
              <Link href={COMPANY_POLICY}>
                <a data-steelboso-id={ButtonCode.BTN_POLICY}>
                  <Typography style={{ textDecoration: 'underline' }} variant={TypoVariant.B4} color={gray.gray6}>
                    {t('signout.link2')}
                  </Typography>
                </a>
              </Link>
            </LinkWrapper>
          </TitleBox>
        </>
        <>
          <ListBox>
            <ListItem>
              <Typography variant={TypoVariant.B4} color={primary.gray}>
                {t('signout.text0-1')}
              </Typography>
              <Typography variant={TypoVariant.B4} color={red.red4}>
                {' '}
                {t('signout.text0-2')}
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant={TypoVariant.B4} color={primary.gray}>
                {t('signout.text1')}
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant={TypoVariant.B4} color={primary.gray}>
                {t('signout.text2')}
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant={TypoVariant.B4} color={primary.gray}>
                {t('signout.text3')}
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant={TypoVariant.B4} color={primary.gray}>
                {t('signout.text4')}
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant={TypoVariant.B4} color={primary.gray}>
                {t('signout.text5')}
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant={TypoVariant.B4} color={primary.gray}>
                {t('signout.text6')}
              </Typography>
            </ListItem>
          </ListBox>
        </>
      </ContentWrapper>

      <DefaultButtonBox
        button1={
          <Button
            text={t('signout.back-button')}
            color={ButtonColor.OutlinedBlack}
            onClick={() => router.back()}
            steelbosoId={ButtonCode.BTN_BACK}
          />
        }
        button2={
          <Button
            text={t('signout.submit-button')}
            color={ButtonColor.OutlinedRed}
            onClick={() => openModal.on()}
            buttonRef={buttonRef}
            steelbosoId={ButtonCode.BTN_MODAL_CONFIRM}
          />
        }
      />

      {/* modal */}
      <SignOutModal modalRef={modalRef} openModal={openModal.isToggled} handleModalClose={() => openModal.off()} />
      {/* modal end */}
    </Layout>
  );
}
